import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/i18n";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Wallet from "@/pages/Wallet";
import Documents from "@/pages/Documents";
import PolicyAnalysis from "@/pages/PolicyAnalysis";
import Chat from "@/pages/Chat";
import Voice from "@/pages/Voice";
import Claims from "@/pages/Claims";
import PremiumChange from "@/pages/PremiumChange";
import Compare from "@/pages/Compare";
import Simulator from "@/pages/Simulator";
import Household from "@/pages/Household";
import Cancellation from "@/pages/Cancellation";
import Checkup from "@/pages/Checkup";
import MissedPayment from "@/pages/MissedPayment";
import Conversations from "@/pages/Conversations";
import SavedAnalyses from "@/pages/SavedAnalyses";
import LifeEvents from "@/pages/LifeEvents";
import SharedReport from "@/pages/SharedReport";
import Protection from "@/pages/Protection";
import Budget from "@/pages/Budget";
import Glossary from "@/pages/Glossary";
import Calculators from "@/pages/Calculators";
import Reminders from "@/pages/Reminders";
import Updates from "@/pages/Updates";
import AdminPortal from "@/pages/AdminPortal";
import DenialFighter from "@/pages/DenialFighter";
import GapRadar from "@/pages/GapRadar";
import Settings from "@/pages/Settings";
import TrustCenter from "@/pages/TrustCenter";

function Loader() {
  return (
    <div className="min-h-screen hero-bg flex items-center justify-center">
      <div className="h-8 w-8 rounded-full border-4 border-blue-200 border-t-brandblue animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/trust" element={<TrustCenter />} />
      <Route path="/admin" element={<AdminPortal />} />
      <Route path="/shared/:token" element={<SharedReport />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
      <Route path="/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/analyze/:id" element={<Protected><PolicyAnalysis /></Protected>} />
      <Route path="/chat" element={<Protected><Chat /></Protected>} />
      <Route path="/voice" element={<Protected><Voice /></Protected>} />
      <Route path="/claims" element={<Protected><Claims /></Protected>} />
      <Route path="/denial-fighter" element={<Protected><DenialFighter /></Protected>} />
      <Route path="/gap-radar" element={<Protected><GapRadar /></Protected>} />
      <Route path="/premium-change" element={<Protected><PremiumChange /></Protected>} />
      <Route path="/compare" element={<Protected><Compare /></Protected>} />
      <Route path="/simulator" element={<Protected><Simulator /></Protected>} />
      <Route path="/cancellation" element={<Protected><Cancellation /></Protected>} />
      <Route path="/checkup" element={<Protected><Checkup /></Protected>} />
      <Route path="/missed-payment" element={<Protected><MissedPayment /></Protected>} />
      <Route path="/conversations" element={<Protected><Conversations /></Protected>} />
      <Route path="/saved" element={<Protected><SavedAnalyses /></Protected>} />
      <Route path="/life-events" element={<Protected><LifeEvents /></Protected>} />
      <Route path="/household" element={<Protected><Household /></Protected>} />
      <Route path="/protection" element={<Protected><Protection /></Protected>} />
      <Route path="/budget" element={<Protected><Budget /></Protected>} />
      <Route path="/glossary" element={<Protected><Glossary /></Protected>} />
      <Route path="/calculators" element={<Protected><Calculators /></Protected>} />
      <Route path="/reminders" element={<Protected><Reminders /></Protected>} />
      <Route path="/updates" element={<Protected><Updates /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App selection-teal">
      <I18nProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </div>
  );
}

export default App;
